import {
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons"
import { Button, Flex, Modal, Skeleton, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import { ActionTooltip } from "../../components/ActionTooltip.tsx"
import { neutral, semantic } from "../../components/widgets/tokens.ts"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import type { RootState } from "../../store.ts"
import { ReportCanvas } from "./components/canvas/ReportCanvas.tsx"
import { ReportCompareControl } from "./components/canvas/ReportCompareControl.tsx"
import { ReportDateRangeControl } from "./components/canvas/ReportDateRangeControl.tsx"
import { ReportFilterBar } from "./components/canvas/ReportFilterBar.tsx"
import { ReportSaveIndicator } from "./components/canvas/ReportSaveIndicator.tsx"
import { ReportScheduleDrawer } from "./components/canvas/ReportScheduleDrawer.tsx"
import { ReportShareModal } from "./components/canvas/ReportShareModal.tsx"
import { ReportToolbar } from "./components/canvas/ReportToolbar.tsx"
import { printReportCanvas } from "./helpers/printReport.ts"
import "./components/canvas/reportPrint.css"
import {
  normalizeDefinition,
  setReportCompare,
  setReportWindow
} from "./helpers/reportDefinition.ts"
import { reportIconKeyOf, ReportIconGlyph } from "./helpers/reportIcons.tsx"
import { useReportCanvasAutosave } from "./hooks/useReportCanvasAutosave.ts"
import { useReportFullscreen } from "./hooks/useReportFullscreen.ts"
import {
  reportCanvasHydrate,
  reportCanvasApplyDefinition,
  reportCanvasRequestRefresh,
  reportCanvasReset
} from "./redux/reportCanvas.slice.ts"
import { reportsClearDetail } from "./redux/reports.slice.ts"
import {
  deleteReport,
  duplicateReport,
  fetchReportDetails,
  toggleReportFavorite
} from "./redux/reports.thunks.ts"
import type { ReportCompareValue, ReportWindow } from "./types/canvas.types.ts"

const { Title, Text } = Typography

const ReportDetailPage = ({
  report,
  fetching,
  getReportDetails,
  clearDetail,
  toggleFavorite
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const canvasRef = useRef<HTMLDivElement>(null)
  const { isFullscreen, toggle: toggleFullscreen } =
    useReportFullscreen(canvasRef)
  const [showShare, setShowShare] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)

  const accessLevel = report?.access_level ?? "owner"
  const canEdit = accessLevel === "owner" || accessLevel === "edit"
  const canDelete = accessLevel === "owner"
  const canShare = accessLevel === "owner"
  const canSchedule = accessLevel === "owner" || accessLevel === "edit"

  useEffect(() => {
    if (!id) return
    getReportDetails(id)
    return () => {
      clearDetail()
      dispatch(reportCanvasReset())
    }
  }, [id, getReportDetails, clearDetail, dispatch])

  useEffect(() => {
    if (!report) return
    dispatch(
      reportCanvasHydrate({
        reportId: report.id,
        definition: normalizeDefinition(report.definition)
      })
    )
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the canvas is hydrated once per report id; a later save replaces the report object and must not reset unsaved canvas edits
  }, [report?.id])

  useEffect(() => {
    generateDocumentTitle(report?.name || t("reports.title"))
  }, [report?.name, t])

  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const { saveStatus, saveError, retry, cancel } = useReportCanvasAutosave(
    report?.id,
    !canEdit
  )

  const handleWindowChange = (window: ReportWindow) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportWindow(definition, window)))
  }

  const handleCompareChange = (compare: ReportCompareValue) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportCompare(definition, compare)))
  }

  const handleDuplicate = async () => {
    if (!report) return
    const action = await dispatch(
      duplicateReport({
        id: report.id,
        name: report.name,
        description: report.description,
        tags: report.tags,
        visibility: report.visibility,
        folder: report.folder,
        definition: definition ?? undefined
      })
    )
    if (duplicateReport.fulfilled.match(action) && action.payload)
      navigate(RoutesPaths.reports.view(action.payload.id))
  }

  const handleDelete = () => {
    if (!report) return
    Modal.confirm({
      title: t("reports.deleteConfirm.title"),
      content: t("reports.deleteConfirm.body", { name: report.name }),
      icon: <ExclamationCircleOutlined />,
      okText: t("reports.deleteConfirm.confirm"),
      okButtonProps: { danger: true },
      cancelText: t("reports.cancel"),
      onOk: async () => {
        // Abandon any pending window/filter autosave so the unmount flush
        // can't PATCH a report that is about to be deleted.
        cancel()
        const action = await dispatch(deleteReport({ id: report.id }))
        if (deleteReport.fulfilled.match(action))
          navigate(RoutesPaths.reports.list)
      }
    })
  }

  return (
    <Flex vertical gap="middle">
      <Flex gap="small" align="center" wrap="wrap">
        {report && (
          <ReportIconGlyph
            iconKey={reportIconKeyOf(definition)}
            style={{ fontSize: 22, color: neutral.muted }}
          />
        )}
        <Flex vertical className="min-w-0 flex-1">
          {fetching && !report ? (
            <Skeleton active title paragraph={{ rows: 1 }} />
          ) : (
            <>
              <Title
                level={4}
                className="m-0"
                ellipsis={{ tooltip: report?.name }}
              >
                {report?.name}
              </Title>
              {report?.description && (
                <Text type="secondary">{report.description}</Text>
              )}
            </>
          )}
        </Flex>

        {report && (
          <Flex align="center" gap="small" wrap="wrap">
            <ActionTooltip
              title={
                report.is_favorite
                  ? t("reports.actions.unfavorite")
                  : t("reports.actions.favorite")
              }
            >
              <Button
                type="text"
                aria-label={
                  report.is_favorite
                    ? t("reports.actions.unfavorite")
                    : t("reports.actions.favorite")
                }
                onClick={() =>
                  toggleFavorite({
                    id: report.id,
                    previousIsFavorite: report.is_favorite
                  })
                }
                icon={
                  report.is_favorite ? (
                    <StarFilled style={{ color: semantic.star }} />
                  ) : (
                    <StarOutlined style={{ color: neutral.faint }} />
                  )
                }
              />
            </ActionTooltip>
            <ReportDateRangeControl
              window={definition?.window}
              onChange={handleWindowChange}
              disabled={!canEdit}
            />
            <ReportCompareControl
              compare={definition?.compare}
              onChange={handleCompareChange}
              disabled={!canEdit}
            />
            {canEdit && (
              <ReportSaveIndicator
                status={saveStatus}
                error={saveError}
                onRetry={retry}
              />
            )}
            <ReportToolbar
              reportId={report.id}
              reportName={report.name}
              saving={false}
              isFullscreen={isFullscreen}
              canEdit={false}
              canDelete={canDelete}
              canShare={canShare}
              canSchedule={canSchedule}
              onRefresh={() => dispatch(reportCanvasRequestRefresh())}
              onSave={() => undefined}
              onAddWidget={() => undefined}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              onToggleFullscreen={toggleFullscreen}
              onShare={() => setShowShare(true)}
              onSchedule={() => setShowSchedule(true)}
              onPrint={printReportCanvas}
            />
            {canEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(RoutesPaths.reports.edit(report.id))}
              >
                {t("reports.detail.edit")}
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(RoutesPaths.reports.create)}
            >
              {t("reports.addNewReport")}
            </Button>
          </Flex>
        )}
      </Flex>

      {report && (
        <ReportFilterBar
          filters={definition?.filters ?? {}}
          onChange={() => undefined}
          disabled
        />
      )}

      {report && (
        <div
          ref={canvasRef}
          className="reports-print-area"
          style={
            isFullscreen
              ? { background: neutral.surface, padding: 16, overflow: "auto" }
              : undefined
          }
        >
          <ReportCanvas readOnly />
        </div>
      )}

      {report && showShare && (
        <ReportShareModal
          reportId={report.id}
          owner={report.owner}
          initialVisibility={report.visibility}
          onClose={() => setShowShare(false)}
          onSaved={() => getReportDetails(report.id)}
        />
      )}

      {report && showSchedule && (
        <ReportScheduleDrawer
          reportId={report.id}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  report: state.reports.detail,
  fetching: state.reports.detailFetching
})

const mapDispatchToProps = {
  getReportDetails: fetchReportDetails,
  clearDetail: reportsClearDetail,
  toggleFavorite: toggleReportFavorite
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportDetailPage)
